// ============================================================================
// PARAMÈTRES DE CAISSE — porté du DOM de la maquette, styles compris.
//
// ⚠️ TROISIÈME ÉCRITURE, ET LA LEÇON EST DE MÉTHODE (2026-09-06). La 1ʳᵉ inventait la mise en page.
// La 2ᵉ reprenait la STRUCTURE rendue par `extract-maquette-screen.mjs` — mais cet outil retire les
// styles, et je m'en étais contenté : divs, badges et champs ne ressemblaient toujours à rien de la
// maquette. Les mesures ci-dessous sont RELEVÉES du DOM inline de `isSet` : colonne de 262 px collante,
// loupe À GAUCHE du champ, badges en pilules de 24 px, sections en cartes `16px 18px 6px`, lignes
// séparées par un filet HAUT, interrupteur 38×22 à curseur 19/3.
//
// ⚠️ CE QUE LA MAQUETTE NE MONTRE PAS, ET QU'IL A FALLU DÉCIDER : chez elle une valeur est une
// PASTILLE EN LECTURE — seuls les interrupteurs réagissent (`setRowGo` ne vise que `[data-setr]`).
// Or cet écran existe pour RÉGLER depuis le back-office. La pastille est donc conservée telle quelle
// au repos, et devient un champ au clic : identique au dessin tant qu'on regarde, utilisable quand
// on agit. Inventer un champ partout aurait trahi le dessin ; s'en tenir à la lecture aurait trahi
// le besoin.
//
// ⚠️ LA PASTILLE « Révision 47 · descendue il y a 6 min » N'EST PAS REPRODUITE À L'IDENTIQUE : ni le
// numéro de révision ni la date de descente ne sont connus du serveur. On occupe sa place avec ce
// qu'on sait VRAIMENT — le nombre de modifications en attente — plutôt que d'afficher un chiffre
// plausible que rien ne mesure (§23.0).
//
// ⚠️ Le bouton « Renvoyer aux caisses » de la maquette est ÉCARTÉ (décision utilisateur) : il fait
// doublon avec le « Tout récupérer » de la caisse. Sa place et son style servent à « Enregistrer ».
//
// ⚠️ LA LIGNE D'UN RÉGLAGE, SON BROUILLON ET LES LISTES SERVIES VIVENT DANS `parametres-reglage.tsx`,
// partagé avec le détail d'une intégration (Intégrations → Wallet → Réglages) : un réglage se rend et
// s'enregistre de la même façon quelle que soit la porte par laquelle on l'atteint. Ici ne restent que
// la mise en page — onglets, recherche, cartes de section — et le catalogue rangé.
// ============================================================================
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { alert, confirm, EmptyState, Icon, Trans, type ConfirmChange } from '@orgazone/ui-web';
import { formatPhoneForDisplay } from '@orgazone/core-crm';
import { switchOn } from '@orgazone/sync-contract';
import { api, type StoreSettingRow, type StoreTopology } from '../api';
import {
  ONGLETS_PARAM, REGLAGES_RANGES, GOUVERNANCE, gouvernanceDe, ongletAVerrou, placeDe, type OngletParam, type ReglageParam, type TabPanel,
} from './parametres-modele';
import { SideNav } from './side-nav';
import { PendingChangesPill, readControlledInput, SettingRow, useStoreSettingsDraft } from './parametres-reglage';
import { deliverySummary } from './reglages-recus';
import { PeripheralFleet } from './peripheriques';
import { StoreMailCard } from './messagerie';
import { ArticleArchive } from './article-archive';
import { key, keyText, t, type Translatable } from '../i18n';

export function ParametresCaisse() {
  // ⚠️ L'ONGLET SE DEMANDE PAR L'ADRESSE (`?onglet=company`) : « Modifier les coordonnées » de la page Magasin ouvre
  //    l'onglet Société, quel que soit celui qu'on a quitté. Un onglet inconnu retombe sur le premier.
  const [params] = useSearchParams();
  const [ongletK, setOngletK] = useState<string>(() => params.get('onglet') ?? ONGLETS_PARAM[0]!.key);
  const [q, setQ] = useState('');
  // Lecture, brouillon et enregistrement : le MÊME chemin que la carte de réglages d'un connecteur.
  const { rows, error, byName, draft, changed, busy, set, discard, save } = useStoreSettingsDraft();
  /**
   * Ce que les caisses ont reçu — relu après chaque enregistrement.
   * ⚠️ Best-effort assumé : un échec de lecture ne retire que la pastille d'état, jamais l'écran de réglages ; elle se
   *    tait plutôt que d'annoncer « à jour » (`deliverySummary`).
   */
  const [topology, setTopology] = useState<StoreTopology | null>(null);
  const relireCaisses = useCallback(() => {
    api.storeTopology().then(setTopology).catch(() => setTopology(null));
  }, []);
  useEffect(() => { relireCaisses(); }, [relireCaisses]);

  const onglet = ONGLETS_PARAM.find((o) => o.key === ongletK) ?? ONGLETS_PARAM[0]!;
  const terme = q.trim().toLowerCase();

  const resultats = useMemo(() => {
    if (!terme) return [];
    return REGLAGES_RANGES.filter(({ reglage }) =>
      t(reglage.label).toLowerCase().includes(terme) || reglage.name.toLowerCase().includes(terme));
  }, [terme]);

  /**
   * ENREGISTRER — rien ne part tant qu'on n'a pas confirmé la liste des changements (dessin).
   * ⚠️ UNE SAISIE ILLISIBLE BLOQUE TOUT LE LOT, nommée : jamais un numéro ou un e-mail cassé ne descend aux caisses, et
   *    l'enregistrement est tout ou rien côté serveur.
   */
  const onSave = async () => {
    const bad = controlledProblems(draft);
    if (bad.length) {
      await alert({ title: t('À corriger avant d’enregistrer'), message: bad.join(' · '), tone: 'warn' });
      return;
    }
    const changes = settingChanges(draft, byName);
    const ok = await confirm({
      title: t('Enregistrer {n} modification ?', { n: changes.length }),
      message: t('Elles s’appliquent aux caisses du magasin à leur prochaine synchronisation.'),
      confirmLabel: t('Enregistrer'), cancelLabel: t('Revenir'), tone: 'info', changes,
    });
    if (ok && await save()) relireCaisses();
  };
  const resume = topology ? deliverySummary(topology, Date.now()) : null;

  return (
    <div>
      {/* En-tête — relevé : gap 16, marge basse 14, colonne de titre à 260 px minimum. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Paramètres')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Réglés ici, <1>appliqués sur les caisses</1> — même découpage que l’écran Configurations de la tablette, où ils s’affichent en lecture.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        {changed.length > 0 ? (
          <>
            <PendingChangesPill count={changed.length} />
            <button type="button" className="oz-discard" onClick={discard} disabled={busy}
              style={{ height: 40, padding: '0 14px', borderRadius: 'var(--r-sm)', background: 'var(--paper)', fontSize: 'var(--t-title)', fontWeight: 600, whiteSpace: 'nowrap', cursor: busy ? 'not-allowed' : 'pointer' }}>
              {t('Annuler les modifications')}
            </button>
          </>
        ) : resume && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 12px', borderRadius: 'var(--r-pill)', background: resume.pending ? 'var(--amber-bg)' : 'var(--teal-100)', color: resume.pending ? 'var(--amber-fg)' : 'var(--teal-800)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: resume.pending ? 'var(--amber)' : 'var(--teal-500)' }} />
            {resume.text}
          </span>
        )}
        <button onClick={() => void onSave()} disabled={!changed.length || busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 18px', border: 'none',
            borderRadius: 'var(--r-sm)', background: 'var(--teal-500)', color: '#fff',
            fontSize: 'var(--t-title)', fontWeight: 600, cursor: changed.length && !busy ? 'pointer' : 'not-allowed',
            opacity: changed.length && !busy ? 1 : 0.5,
          }}>
          <Icon name="save" size={15} stroke={1.9} />
          {busy ? t('Enregistrement…') : t('Enregistrer')}
        </button>
      </div>

      {error != null ? (
        <EmptyState icon="alert" title={t('Réglages indisponibles')} note={error} />
      ) : rows === null ? (
        <div style={{ padding: 34, textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Chargement…')}</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Colonne gauche — la colonne partagée avec les Référentiels : recherche puis onglets. Pendant une recherche,
              elle ne garde que les onglets dont le nom, ou un réglage, porte le terme (maquette, `setTabs`). */}
          <SideNav
            query={q} onQuery={setQ} placeholder={t('Rechercher un réglage…')} label={t('Onglets des paramètres')}
            active={terme ? null : onglet.key}
            onSelect={(k) => { setQ(''); setOngletK(k); }}
            empty={t('Aucun onglet ne porte ce terme')}
            groups={[{ title: null, items: ONGLETS_PARAM
              .filter((o) => !terme || t(o.label).toLowerCase().includes(terme) || resultats.some((r) => r.onglet.key === o.key))
              .map((o) => ({ key: o.key, label: t(o.label), icon: o.icon, locked: ongletAVerrou(o) })) }]}
          />

          {/* Panneau droit — une COLONNE de cartes espacées de 10, pas une carte unique. */}
          <div style={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {terme ? (
              <ResultatsRecherche resultats={resultats} onAller={(k) => { setQ(''); setOngletK(k); }} />
            ) : (
              <TabContent onglet={onglet} renderSetting={(view) => {
                const state = byName.get(view.name);
                if (!state) return null; // le catalogue ne le sert pas : on n'invente pas de ligne
                return <SettingRow key={view.name} view={view} state={state} input={draft[view.name]} onChange={set} />;
              }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * LE CONTENU D'UN ONGLET : son en-tête, ses sections de réglages, puis le bloc qu'il porte s'il en a un.
 * ⚠️ Exporté pour être RENDU par son banc : l'écran entier attend le réseau avant de montrer un onglet.
 */
export function TabContent({ onglet, renderSetting }: {
  onglet: OngletParam; renderSetting: (view: ReglageParam) => ReactNode;
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 16px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 200 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{t(onglet.label)}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{t(onglet.note)}</span>
        </span>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['verrouille', 'bo', 'caisse'] as const).map((g) => (
            <span key={g} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 9px', borderRadius: 'var(--r-pill)', background: GOUVERNANCE[g].bg, color: GOUVERNANCE[g].fg, fontSize: 'var(--t-sub)', fontWeight: 600 }}>
              {g === 'verrouille' && <Icon name="lock" size={10} stroke={2.4} />}
              {t(GOUVERNANCE[g].label)}
            </span>
          ))}
        </span>
      </div>

      {onglet.sections.map((s) => (
        <div key={keyText(s.titre)} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', padding: '16px 18px 6px' }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t(s.titre)}</div>
          <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{t(s.note)}</div>
          <div style={{ marginTop: 12 }}>
            {s.reglages.map(renderSetting)}
          </div>
        </div>
      ))}
      {onglet.panel && <TabPanelView panel={onglet.panel} />}
    </>
  );
}

/**
 * LES BLOCS QU'UN ONGLET PORTE — un titre, une note et le composant. ⚠️ Fermé par le type : un bloc déclaré au
 * modèle sans entrée ici ne compile pas. ⚠️ Un bloc SANS titre est le contenu même de l'onglet (l'archivage) : l'en-tête
 * de l'onglet le nomme déjà, un second titre le répéterait.
 */
const PANELS: Record<TabPanel, { title?: Translatable; note?: Translatable; Body: () => ReactNode }> = {
  peripheralFleet: {
    title: key('Parc du magasin'),
    note: key('Les terminaux de paiement et les imprimantes du magasin, et la caisse à laquelle chacun est branché.'),
    Body: PeripheralFleet,
  },
  storeMail: {
    title: key('Envoi des documents du magasin'),
    note: key('D’où partent les documents de ce magasin — le procès-verbal d’inventaire, par exemple. À ne pas confondre avec l’envoi des tickets par la caisse, ci-dessus.'),
    Body: StoreMailCard,
  },
  articleArchive: { Body: ArticleArchive },
};

/** Le bloc d'un onglet : sa carte de titre, au rembourrage des sections, puis son contenu. */
export function TabPanelView({ panel }: { panel: TabPanel }) {
  const { title, note, Body } = PANELS[panel];
  return (
    <>
      {title !== undefined && (
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', padding: '16px 18px' }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t(title)}</div>
          {note !== undefined && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{t(note)}</div>}
        </div>
      )}
      <Body />
    </>
  );
}

/** Les résultats de la recherche GLOBALE — chacun dit son onglet, et y mène. */
function ResultatsRecherche({ resultats, onAller }: {
  resultats: typeof REGLAGES_RANGES; onAller: (ongletKey: string) => void;
}) {
  if (!resultats.length) {
    return (
      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)' }}>
        <EmptyState icon="search" title={t('Aucun réglage trouvé')} note={t('Aucun réglage ne porte ce terme, ni dans son nom ni dans son libellé.')} />
      </div>
    );
  }
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
        {t('{n} réglage trouvé', { n: resultats.length })}
      </div>
      {resultats.map(({ reglage, onglet, section }) => (
        <button key={reglage.name} onClick={() => onAller(onglet.key)} style={{
          display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '11px 16px', border: 'none',
          borderBottom: '1px solid var(--ink-100)', background: 'transparent', cursor: 'pointer', textAlign: 'left',
        }}>
          <Icon name={onglet.icon} size={16} stroke={1.8} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{t(reglage.label)}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t(onglet.label)} · {t(section.titre)}</span>
          </span>
          <Icon name="chevronR" size={15} stroke={2} color="var(--ink-400)" />
        </button>
      ))}
    </div>
  );
}

/**
 * LES CHANGEMENTS À CONFIRMER — un par réglage du brouillon : son libellé, sa gouvernance, l'ancienne et la nouvelle valeur.
 * ⚠️ L'ANCIENNE VALEUR EST CE QUE LE SERVEUR TIENT : « défaut de la caisse » quand rien n'est posé, pour qu'on ne confonde
 *    pas vider un réglage et revenir au défaut.
 */
export function settingChanges(draft: Record<string, string>, byName: Map<string, StoreSettingRow>): ConfirmChange[] {
  return Object.entries(draft).map(([name, after]) => {
    const view = placeDe(name)?.reglage ?? null;
    const row = byName.get(name);
    return {
      label: view ? t(view.label) : name,
      detail: row ? t(GOUVERNANCE[gouvernanceDe(name, row.scope)].label) : undefined,
      before: valueText(view, row?.value ?? null),
      after: valueText(view, after),
    };
  });
}

/** Une valeur dans la liste des changements — l'interrupteur en mots, le téléphone groupé, le vide et le défaut nommés. */
function valueText(view: ReglageParam | null, value: string | null): string {
  if (view?.forme === 'toggle') return switchOn(view.name, value) ? t('Activé') : t('Désactivé');
  if (value === null) return t('défaut de la caisse');
  if (value === '') return t('(vide)');
  if (view?.forme === 'secret') return '••••••••';
  return view?.forme === 'telephone' ? formatPhoneForDisplay(value) : value;
}

/** Les saisies contrôlées illisibles du brouillon, nommées — l'alerte qui bloque l'enregistrement les liste. */
export function controlledProblems(draft: Record<string, string>): string[] {
  return Object.entries(draft).flatMap(([name, value]) => {
    const view = placeDe(name)?.reglage;
    if (view?.forme !== 'telephone' && view?.forme !== 'email') return [];
    const check = readControlledInput(view.forme, value);
    return check.bad ? [`${t(view.label)} — ${check.hint}`] : [];
  });
}
